import { type ActorEvent, EVENT_TYPE, type Field, type FieldADTValue, type RecordLikeProtocol, type ViewField } from '@lighthouse/core'
import { fieldConvertValue, FieldValuePreviewer, useActionRunningLoadings } from '@lighthouse/shared'
import type { Vector2 } from '@use-gesture/react'
import { useDrag, useGesture } from '@use-gesture/react'
import { addDays, differenceInHours, isAfter, lightFormat, parseISO, startOfDay } from 'date-fns'
import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLatest } from 'react-use'

import { useViewBlockContext } from '../../../context'
import { CELL_HEIGHT, DAY_MARGIN_BOTTOM, DAY_MARGIN_TOP, LINE_GAP, MINI_CELL_HEIGHT } from '../Cell'
import * as SC from './TimeLine.style'
import type { TimeLineInfo } from './TimeLine.type'

interface TimeLineProps extends React.ComponentPropsWithoutRef<'div'> {
    schema: Record<string, Field>
    record: RecordLikeProtocol
    columns: ViewField[]

    prevHeight?: number
    onHeightChange: (v: number) => void

    info: TimeLineInfo
    tableElement: React.MutableRefObject<HTMLTableElement | null>

    isMobile: boolean
    editable: boolean
    hoveredLine: string

    onResizeStart: () => void
    onResizeEnd: () => void
    onDragStart: () => void
    onDragEnd: () => void

    onUpdateDate: (id: string, startDate: Date, endDate: Date) => void
    onUpdateLocalDate: (id: string, startDate: Date, endDate: Date) => void
    onClickSchedule?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
}

export const TimeLine = forwardRef<HTMLDivElement, TimeLineProps>((props, ref) => {
    const {
        schema,
        record,
        columns,

        prevHeight = 0,
        onHeightChange,

        info,
        tableElement,

        isMobile,
        editable,
        hoveredLine,

        onResizeStart,
        onResizeEnd,
        onDragStart,
        onDragEnd,

        onUpdateDate,
        onUpdateLocalDate,
        onClickSchedule,

        style,
        ...rest
    } = props

    const [position, setPosition] = useState({ x: 0, y: 0, width: 0 })
    const { blockData, uniqueId } = useViewBlockContext()
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { id, config } = blockData
    const { eventList } = config
    const recordClickEvent = useMemo(() => eventList?.find(item => item.type === EVENT_TYPE.RECORD_CLICK), [eventList])
    const lineRef = useRef<HTMLDivElement | null>(null)
    const latest = useLatest({ onHeightChange, onUpdateDate, onUpdateLocalDate })

    // 监听宽度变化
    useLayoutEffect(() => {
        if (!tableElement.current) {
            return
        }

        const resizeHandler: ResizeObserverCallback = ([e]) => {
            const tableRect = e.target.getBoundingClientRect()

            const dateStr = lightFormat(info.dates[0], 'yyyy-MM-dd')

            const cell = tableElement.current?.querySelector(`[data-value="${dateStr}"]`)
            if (!cell) {
                return
            }

            const rect = cell.getBoundingClientRect()
            setPosition({
                x: rect.left - tableRect.left,
                y:
                    rect.top -
                    tableRect.top +
                    prevHeight +
                    info.index * LINE_GAP +
                    (isMobile ? MINI_CELL_HEIGHT : CELL_HEIGHT) +
                    DAY_MARGIN_TOP +
                    DAY_MARGIN_BOTTOM,
                width: rect.width * info.dates.length
            })
        }

        const observer = new ResizeObserver(resizeHandler)

        observer.observe(tableElement.current)

        return () => {
            observer.disconnect()
        }
    }, [info.dates, info.index, isMobile, prevHeight, tableElement])

    const hasRendered = !!tableElement.current

    // 监听自身高度
    useLayoutEffect(() => {
        const el = lineRef.current
        if (!el) {
            return
        }

        const handler: ResizeObserverCallback = entries => {
            const [e] = entries
            latest.current.onHeightChange(e.target.clientHeight)
        }

        const observer = new ResizeObserver(handler)

        observer.observe(el)

        return () => {
            observer.unobserve(el)
        }
    }, [latest, info.index, hasRendered])

    // const resizeHandleRef = useRef<HTMLDivElement | null>(null)
    const stateRef = useRef({ isResizing: false, isMoving: false })

    const updateTimeLine = useCallback(
        (values: Vector2) => {
            const [x, y] = values
            if (!tableElement.current) {
                return
            }

            const cells = tableElement.current.querySelectorAll('td > div')

            for (let index = 0; index < cells.length; index++) {
                const el = cells.item(index)
                const rect = el.getBoundingClientRect()

                if (rect.left < x && rect.right > x && rect.top < y && rect.bottom > y) {
                    const str = el.getAttribute('data-value')
                    if (!str) {
                        return
                    }

                    const oldEndDate = info.originDates[info.originDates.length - 1]
                    const startDate = startOfDay(parseISO(str))

                    const maxDate = new Date(oldEndDate)
                    maxDate.setHours(0)
                    maxDate.setMinutes(0)
                    maxDate.setMilliseconds(0)
                    if (isAfter(startDate, maxDate)) {
                        return
                    }

                    stateRef.current.isResizing
                        ? latest.current.onUpdateLocalDate(info.id, startDate, oldEndDate)
                        : latest.current.onUpdateDate(info.id, startDate, oldEndDate)
                    break
                }
            }
        },
        [info.id, info.originDates, latest, tableElement]
    )

    const resizeHandleBind = useGesture(
        {
            onDrag: state => {
                state.event.stopPropagation()
                if (state.first) {
                    onResizeStart()
                    stateRef.current.isResizing = true

                    return
                }

                if (state.last) {
                    onResizeEnd()
                    stateRef.current.isResizing = false
                    updateTimeLine(state.values)
                    return
                }

                updateTimeLine(state.values)
            }
        },
        {
            enabled: editable && !isMobile
        }
    )

    // 移动日程
    const moveHandle = useCallback(
        (values: Vector2) => {
            const [x, y] = values
            if (!tableElement.current) {
                return
            }

            const cells = tableElement.current.querySelectorAll('td > div')

            for (let index = 0; index < cells.length; index++) {
                const el = cells.item(index)
                const rect = el.getBoundingClientRect()

                if (rect.left < x && rect.right > x && rect.top < y && rect.bottom > y) {
                    const str = el.getAttribute('data-value')
                    if (!str) {
                        return
                    }

                    const startDate = startOfDay(parseISO(str))
                    const oldStartDate = info.originDates[0]
                    const oldEndDate = info.originDates[info.originDates.length - 1]
                    const diff = Math.floor(differenceInHours(startDate, oldStartDate) / 24) | 0

                    // if (diff === 0) {
                    //     return
                    // }

                    const endDate = addDays(oldEndDate, diff)

                    stateRef.current.isMoving
                        ? latest.current.onUpdateLocalDate(info.id, startDate, endDate)
                        : latest.current.onUpdateDate(info.id, startDate, endDate)
                    break
                }
            }
        },
        [info.id, info.originDates, latest, tableElement]
    )

    const [mousePosition, setMousePosition] = useState({ x: -999, y: -999 })
    const [isDragging, setIsDragging] = useState(false)

    const bind = useDrag(
        state => {
            if (state.tap) {
                if (!recordClickEvent || !onClickSchedule) {
                    return
                }
                handleActionTriggerWithLoading({
                    id: uniqueId,
                    actorEvent: recordClickEvent,
                    trigger: (id, actorEvent) => onClickSchedule?.(id, actorEvent, info.record)
                })
                return
            }

            if (isMobile) {
                return
            }

            if (stateRef.current.isResizing) {
                return
            }

            if (state.first) {
                onDragStart()
                setIsDragging(true)
                stateRef.current.isMoving = true
                return
            }

            if (state.last) {
                onDragEnd()
                stateRef.current.isMoving = false
                moveHandle(state.values)

                setIsDragging(false)
                setMousePosition({ x: -999, y: -999 })
                return
            }
            setMousePosition({ x: state.values[0], y: state.values[1] })
            // moveHandle(state.values)
        },
        {
            enabled: editable,
            filterTaps: true,
            pointer: {
                capture: false
            }
        }
    )

    // 提前过滤，判断内容是否为空
    const fields = useMemo(() => {
        return columns.filter(col => {
            const field = schema[col.fieldId]
            const data = record.content?.[col.fieldId]?.value
            const fieldValue = { ...field, value: data } as FieldADTValue
            const realValue = fieldConvertValue(fieldValue)
            return field && realValue
        })
    }, [columns, record.content, schema])

    if (!tableElement.current) {
        return null
    }

    const isFirstSegment = info.dates[0] === info.originDates[0]

    const dom = (
        <SC.Line
            {...bind()}
            ref={ref}
            color={info.color}
            showBar={false}
            data-ignore-tap
            hovered={hoveredLine === info.id}
            style={{
                opacity: position.width === 0 ? 0 : 1,
                left: position.x,
                top: position.y,
                width: position.width,
                height: isMobile ? 22 : undefined,
                ...style
            }}
            {...rest}
        >
            <SC.LineWrapper ref={lineRef}>
                {!isMobile && editable && isFirstSegment && <SC.LineResizeHandle {...resizeHandleBind()} />}
                {isFirstSegment && <SC.LineTitle isMobile={isMobile}>{info.title}</SC.LineTitle>}

                {isFirstSegment && !isMobile && fields.length > 0 ? (
                    <SC.LineContent>
                        {fields.map(col => {
                            const field = schema[col.fieldId]
                            const data = record.content?.[col.fieldId]?.value

                            return <FieldValuePreviewer key={col.fieldId} field={field} data={data} />
                        })}
                    </SC.LineContent>
                ) : null}
            </SC.LineWrapper>
        </SC.Line>
    )

    const overlayDom = isDragging
        ? React.cloneElement(dom, {
              style: { pointerEvents: 'none', width: position.width, top: mousePosition.y, left: mousePosition.x, opacity: 0.5 }
          })
        : null

    return (
        <>
            {createPortal(
                dom,

                tableElement.current.parentElement!
            )}

            {createPortal(overlayDom, document.body)}
        </>
    )
})
